import { gql, useApolloClient, useMutation } from '@apollo/client';
import IconPlus from '@apollo/icons/default/IconPlus.svg';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import {
  Alert,
  Avatar,
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  CardTitle,
  Group,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

import { variantHomepageRouteConfig } from 'src/app/graph/routes';
import { RoverAddSubgraphModal } from 'src/app/graph/subgraphsLayout/subgraphsPage/roverAddSubgraphModal/RoverAddSubgraphModal';
import { MarbledAvatar } from 'src/components/common/MarbledAvatar';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { match } from 'src/components/common/typeahead/fuzzy';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import Config from 'src/lib/config';
import * as GraphQLTypes from 'src/lib/graphqlTypes/types';

export const NoVariantsSupergraphsListItem = ({
  graphId,
  graphTitle,
  graphType,
  avatarUrl,
  searchText,
}: {
  graphId: string;
  graphTitle: string;
  graphType: GraphQLTypes.GraphType;
  avatarUrl: string | null;
  searchText?: string;
}) => {
  const client = useApolloClient();
  const [inViewRef, inView] = useInView();

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [addSubgraphOpen, setAddSubgraphOpen] = React.useState(false);
  const [deleteGraph, { error, loading }] = useMutation<
    GraphQLTypes.NoVariantsSupergraphDeleteMutation,
    GraphQLTypes.NoVariantsSupergraphDeleteMutationVariables
  >(
    gql`
      mutation NoVariantsSupergraphDeleteMutation($graphId: ID!) {
        service(id: $graphId) {
          delete
        }
      }
    `,
    {
      onCompleted: () => {
        // Delete the `Service` from the cache
        client.cache.evict({
          id: client.cache.identify({
            __typename: 'Service',
            id: graphId,
          }),
        });
        client.cache.gc();
      },
    },
  );

  return (
    <>
      <Modal
        isOpen={deleteOpen}
        size="lg"
        scrollBehavior="inside"
        onClose={() => setDeleteOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              Are you sure you want to delete {graphTitle}?
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            This action cannot be undone.
            {error && <Alert status="error">{error.message}</Alert>}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                isLoading={loading}
                loadingText="Yes, delete"
                onClick={() => {
                  deleteGraph({ variables: { graphId } })
                    .then(() => {
                      setDeleteOpen(false);
                    })
                    .catch((e) => {
                      Sentry.captureException(e);
                    });
                }}
              >
                Yes, delete
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {addSubgraphOpen && (
        <RoverAddSubgraphModal
          isOpen={addSubgraphOpen}
          onClose={() => setAddSubgraphOpen(false)}
          graphRef={{ graphId, graphVariant: Config.service.defaultVariant }}
          withNewVariant={true}
          title={`Add a variant to ${graphTitle}`}
          description="Follow the steps below to publish a subgraph that you have running in the cloud of your choice to a new variant:"
        />
      )}

      <Card className="mb-4 overflow-visible" ref={inViewRef}>
        <CardHeader className="flex-row items-center">
          <Group size="fill" gap={4}>
            {avatarUrl ? (
              <Avatar src={avatarUrl} variant="graph" />
            ) : (
              <MarbledAvatar
                animate={inView}
                identifier={graphId}
                className="flex-none"
              />
            )}
            <CardTitle>
              <Link
                data-testid={`supergraph-list-variant-link-${graphId}-${Config.service.defaultVariant}`}
                to={variantHomepageRouteConfig.location({
                  graphId,
                  graphVariant: Config.service.defaultVariant,
                })}
                className="break-all underline"
              >
                {searchText ? (
                  <span>
                    {
                      match(searchText, graphTitle, {
                        elem: (
                          <span className="bg-searchhighlight text-inverted" />
                        ),
                      })?.rendered
                    }
                  </span>
                ) : (
                  graphTitle
                )}
              </Link>
            </CardTitle>
          </Group>
          <ButtonGroup>
            {graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH ? (
              <PermissionWarning
                graphId={graphId}
                graphPermissions="canDelete"
                childRenderer={(hasPermission) => (
                  <Tooltip
                    label={
                      hasPermission
                        ? 'Add variant'
                        : 'You do not have permission to add a variant. Please contact your org admin.'
                    }
                  >
                    <IconButton
                      aria-label="Add variant"
                      variant="secondary"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setAddSubgraphOpen(true);
                      }}
                      icon={<IconPlus />}
                      isDisabled={!hasPermission}
                    />
                  </Tooltip>
                )}
              />
            ) : (
              <Tooltip label={`Finish setup for ${graphTitle}`}>
                <Button
                  variant="secondary"
                  as={Link}
                  to={variantHomepageRouteConfig.location({
                    graphId,
                    graphVariant: Config.service.defaultVariant,
                  })}
                >
                  Finish setup
                </Button>
              </Tooltip>
            )}
            {inView ? (
              <PermissionWarning
                graphId={graphId}
                graphPermissions="canDelete"
                childRenderer={(hasPermission) => (
                  <Tooltip
                    shouldWrapChildren
                    disabled={!hasPermission}
                    label={`Delete ${
                      graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH
                        ? 'supergraph'
                        : 'graph'
                    }`}
                  >
                    <IconButton
                      aria-label={`Delete ${
                        graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH
                          ? 'supergraph'
                          : 'graph'
                      }`}
                      variant="destructive"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteOpen(true);
                      }}
                      icon={<IconTrash />}
                      isDisabled={!hasPermission}
                    />
                  </Tooltip>
                )}
              />
            ) : (
              <IconButton
                aria-label={`Delete ${
                  graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH
                    ? 'supergraph'
                    : 'graph'
                }`}
                variant="destructive"
                icon={<IconTrash />}
                isDisabled={true}
              />
            )}
          </ButtonGroup>
        </CardHeader>
      </Card>
    </>
  );
};
